import { dataService } from '../../data';
import { TaxIdValidators } from '@unpispas/upp-defs';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { Place } from './place';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { ClientType } from '../types';

const _schema = {
    "name": "INVOICEBUSINESS",
    "fields": [
        { name: "place", type: "objid" },
        { name: "status", type: "string", default: "AC" },
        { name: "next", type: "objid" },
        { name: "name", type: "string" },
        { name: "taxid", type: "string" },
        { name: "address", type: "string" },
        { name: "email", type: "string" },
        { name: "postal", type: "string" },
        { name: "region", type: "string" },
        { name: "country", type: "string" },
        { name: "invoiced", type: "date" }
    ],
    relate: [
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: ">", target: "INVOICEBUSINESS", by: "next", name: "next", reason: "next", child: false, class: ClientType },
    ]
} as const;

const _ClientClass = CreateObject(_schema);
if (!_ClientClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Client extends _ClientClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Client {
        return this._Copy(store) as Client;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddClient(this);
            }
        }
    }

    get taxid(): string | null {
        return super.taxid as string | null;
    }

    set taxid(value: string | null){
        const _rawTaxId = String(value || '').trim();
        const _taxid = TaxIdValidators.ValidateTaxId(_rawTaxId) ? (TaxIdValidators.ToTaxId(_rawTaxId) || '') : _rawTaxId;
        super.taxid = _taxid;
    }

    get IsValid(): boolean {
        if (!this.status || (this.status != 'AC')){
            return false;
        }

        return !!this.name && !!this.taxid;
    }

    /**
     * Follows the `next` replacement chain to the current fiscal client (paridad `Product.last`).
     */
    get last(): Client {
        if (!this.next){
            return this;
        }

        return (this.next as Client).last;
    }

    /**
     * Returns whether this client and `other` represent the same fiscal business identity
     * in the local catalog (match by trimmed `name` and `taxid`).
     *
     * Used to deduplicate `INVOICEBUSINESS` when linking accounts or invoices (paridad v3
     * `_CreateBusiness`). Does not compare address or other fields; a different address
     * on a matching identity is handled separately via the `next` replacement chain.
     *
     * @param other - Client to compare against, or `null`.
     * @returns `true` when both have the same non-empty name and taxid after trim.
     *          `taxid` is compared as stored; normalization happens on assign via the setter.
     */
    Same(other: Client | null): boolean {
        if (!other){
            return false;
        }

        const _name = String(this.name ?? '').trim();
        const _otherName = String(other.name ?? '').trim();
        if (!_name || !_otherName || (_name !== _otherName)){
            return false;
        }

        const _taxid = String(this.taxid ?? '').trim();
        const _otherTaxid = String(other.taxid ?? '').trim();

        return !!(_taxid && _otherTaxid && (_taxid === _otherTaxid));
    }
}
